import { ReactNode, useEffect, useMemo, useState } from 'react';
import createSafeContext from '@paradigm/utils/src/createSafeContext';
import { useObservable } from '@paradigm/utils/src/hooks';

import UnifiedRfqsListService from '#/unified-rfqs/services/unified-rfqs';

import { useRfqFiltersValue } from '#/unified-rfqs/ui/rfq-filters/filter-provider';
import { useGrfqService } from '#/unified-rfqs/contexts/grfqs';
import { useDrfqService } from '#/unified-rfqs/contexts/drfqs';
import RfqsStore from '#/unified-rfqs/store/rfqs';
import { useApi } from '@paradigm/api/src/fetch-api';
import { useInterval } from '@paradigm/utils/src/useInterval';
import { Instrument } from '../entities/drfq/instruments';

export const [useUnifiedRfqsService, BaseRfqServiceProvider] =
  createSafeContext<UnifiedRfqsListService>('RfqService');

interface Props {
  readonly children: ReactNode;
  readonly rfqsStore: RfqsStore;
}

export function UnifiedRfqsServiceProvider({ children, rfqsStore }: Props) {
  const grfqService = useGrfqService();
  const drfqService = useDrfqService();
  const { drfqFutureInstruments } = useDrfqFutureInstruments();

  const {
    state: {
      auctionTypes,
      productCodes,
      strategyCodes,
      notional,
      counterparties,
    },
  } = useRfqFiltersValue();
  const rfqService = useMemo(
    () =>
      new UnifiedRfqsListService({
        drfqService,
        grfqService,
        rfqsStore,
        filters: {
          auctionTypes,
          productCodes,
          strategyCodes,
          notional,
          counterparties: counterparties.map((s) => s.value),
        },
      }),
    [
      drfqService,
      grfqService,
      rfqsStore,
      auctionTypes,
      productCodes,
      strategyCodes,
      notional,
      counterparties,
    ],
  );
  useEffect(() => {
    rfqService.setDrfqFutureInstruments(drfqFutureInstruments);
  }, [drfqFutureInstruments, rfqService]);

  return (
    <BaseRfqServiceProvider key={rfqService.id} value={rfqService}>
      {children}
    </BaseRfqServiceProvider>
  );
}

export function useRfqsFetchData() {
  const { fetcher } = useUnifiedRfqsService();
  return useObservable(fetcher.fetchData);
}

export function useRfqIdsList() {
  const { fetcher } = useUnifiedRfqsService();
  const [rfqIds, setRfqIds] = useState(fetcher.selectPaginatedIds());
  useEffect(() => {
    const unsub = fetcher.subscribePaginatedIds(setRfqIds);
    return () => {
      unsub();
    };
  }, [fetcher]);
  return rfqIds;
}

function useDrfqFutureInstruments() {
  const drfqService = useDrfqService();
  const [, fetchDrfqInstruments] = useApi(drfqService.listInstruments);
  const [drfqFutureInstruments, setDrfqFutureInstruments] = useState<
    readonly Instrument[]
  >([]);

  useInterval(() => {
    if (process.env.NODE_ENV === 'test') return;
    fetchDrfqInstruments({ kind: 'FUTURE', includeGreeks: true }, (resp) => {
      if (resp.ok) {
        setDrfqFutureInstruments(resp.data.results);
      }
    });
  }, 15_000);

  return { drfqFutureInstruments };
}
